import { inject, NgModule } from '@angular/core';
import { ResolveFn, RouterModule, Routes } from '@angular/router';
import {
  AuthGuard,
  PopulationGuard,
  MemberResolver,
  MemberCardResolver,
  BalanceResolver,
  CardTokenResolver,
  FantasyGameResolver,
  SurveyResolver,
  AddressResolver,
  CountryResolver,
  PortalProductsSubscriptionsResolver,
  ProductCodeGuard,
  StepType,
  RegistrationStepResolver,
  firstTimeUserGuard,
  PopulationQuery,
  ChildService,
} from '@fgb/core';

// General
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

// Loyalty
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { BadgesPageComponent } from './pages/badges-page/badges-page.component';

// Account
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';
// Ecash
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
// Portal Products
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
// Events & Tickets

// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
// Club Pages

// Partners Pages
import { TmCallbackComponent } from './shared/components/manage-tickets/tm-callback/tm-callback.component';
import { TmAuthCallbackComponent } from './shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { SubscriptionsPageComponent } from './pages/subscriptions-page/subscriptions-page.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
// Offer Pages
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { NotificationManagementPageComponent } from './pages/notification-management-page/notification-management-page.component';
import { SportsAllianceCallbackComponent } from './shared/components/providers/sports-alliance/sports-alliance-callback/sports-alliance-callback.component';
import { ProviderCallbackComponent } from './shared/components/providers/provider-callback/provider-callback.component';
import { PhotoScramblePageComponent } from './pages/photo-scramble-page/photo-scramble-page.component';
import { PlayerRevealPageComponent } from './pages/player-reveal-page/player-reveal-page.component';
import { Populations } from './shared/utilities/populations';
import { ProductCode } from './shared/utilities/product-code';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import {
  provideRegistrationConfig,
  RegistrationPaymentCallbackPageComponent,
} from '@fgb/portal-component-library/src/lib/registration';
import { RegisterSuccessComponent } from './pages/payment-page/register-success/register-success.component';

import { YouthBaseballPageComponent } from './pages/youth-baseball/youth-baseball-page.component';
import { RegistrationOptOutAlertsComponent } from './modules/registration/pages/steps/registration-opt-out-alerts/registration-opt-out-alerts.component';
import { CustomRegistrationPaymentComponent } from './modules/registration/pages/steps/registration-payment/registration-payment.component';
import { canMatchGuardian } from './shared/matcher';
import { canMatchKids } from './shared/matcher/kids.match';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { QuizLayoutPageComponent } from './pages/quiz-page/quiz-layout-page/quiz-layout-page.component';
import { QuizLeaderboardPageComponent } from './pages/quiz-page/quiz-leaderboard-page/quiz-leaderboard-page.component';
import { AceJumpPageComponent } from './pages/ace-jump-page/ace-jump-page.component';
import { AceJumpLayoutPageComponent } from './pages/ace-jump-page/ace-jump-layout-page/ace-jump-layout-page.component';
import { AceJumpLoaderboardPageComponent } from './pages/ace-jump-page/ace-jump-loaderboard-page/ace-jump-loaderboard-page.component';
import { PhotoScrambleLayoutPageComponent } from './pages/photo-scramble-page/photo-scramble-layout-page/photo-scramble-layout-page.component';
import { PlayerRevealLayoutPageComponent } from './pages/player-reveal-page/player-reveal-layout-page/player-reveal-layout-page.component';
import { PlayerRevealLeaderboardPageComponent } from './pages/player-reveal-page/player-reveal-leaderboard-page/player-reveal-leaderboard-page.component';
import { lastValueFrom } from 'rxjs';
import { PhotoScrambleLeaderboardPageComponent } from './pages/photo-scramble-page/photo-scramble-leaderboard-page/photo-scramble-leaderboard-page.component';

// Helper to pre-fetch children if user is a guardian
const fetchChildrenResolver: ResolveFn<void | Promise<any>> = () => {
  const populationQuery = inject(PopulationQuery);
  const isGuardian = populationQuery.userInPopulation([Populations.Guardian]);
  if (isGuardian) {
    const childService = inject(ChildService);
    lastValueFrom(childService.fetchChildren());
  }
  return;
};

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard()],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      address: AddressResolver,
      // Pre-fetch children if user is guardian, this is for the offer discount to work as offers need to show if child is more than 1
      children: fetchChildrenResolver,
    },
    children: [
      {
        path: '',
        // Redirect to guardian home page if user is a guardian
        canMatch: [canMatchGuardian],
        loadComponent: () =>
          import('./pages/home-page/guardians-home-page/guardians-home-page.component').then((m) => m.GuardiansHomePageComponent),
      },
      {
        path: '',
        // Redirect to kids home page if user is a kid
        canMatch: [canMatchKids],
        loadComponent: () =>
          import('./pages/home-page/kids-home-page/kids-home-page.component').then((m) => m.KidsHomePageComponent),
      },
      {
        path: '',
        // Legacy home page, shows if user is neither a guardian nor a kid
        canActivate: [PopulationGuard],
        data: {
          exclude: ['testPop'],
          popRedirect: 'test',
        },
        component: HomePageComponent,
      },
      {
        path: 'guardianPage',
        component: GuardianPageComponent,
        resolve: {
          country: CountryResolver,
          address: AddressResolver,
        },
      },
      {
        path: 'youth',
        component: YouthBaseballPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Guardian'],
          popRedirect: '',
        },
      },
      {
        path: 'worksheet',
        component: WorksheetPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Guardian'],
          popRedirect: '',
        },
      },
      {
        path: 'account',
        component: AccountDetailsPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Kids'],
          popRedirect: '',
        },
        resolve: {
          member: MemberResolver,
        },
      },
      {
        path: 'portalproducts',
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: PortalProductSuccessPageComponent,
          },
          { path: 'failure', component: PortalProductFailurePageComponent },
          { path: 'result', component: PortalProductResultsPageComponent },
        ],
      },
      {
        path: 'cardtoken',
        children: [
          { path: 'success', component: EcashSuccessPageComponent },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },
      {
        path: 'guardians',
        redirectTo: '',
        pathMatch: 'full',
        resolve: {
          addresses: AddressResolver,
          member: MemberResolver,
        },
        component: GuardianPageComponent,
      },
      {
        path: 'terms',
        component: TermsPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Kids'],
          popRedirect: '',
        },
      },
      {
        path: 'badges',
        component: BadgesPageComponent,
        canActivate: [PopulationGuard, ProductCodeGuard],
        data: {
          exclude: [Populations.Guardian],
          excludeCodes: ProductCode.FreeKid,
          popRedirect: '',
          codesRedirect: '',
        },
      },
      {
        path: 'challenges',
        component: ChallengesPageComponent,
        canActivate: [ProductCodeGuard],
        data: {
          includeCodes: ProductCode.PaidKid,
          codesRedirect: '',
        },
      },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      {
        path: 'games',
        resolve: {
          surveys: SurveyResolver,
          games: FantasyGameResolver,
        },
        component: GamesLayoutComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Guardian'],
          popRedirect: '',
        },
        children: [
          { path: '', component: GamesPageComponent },
          {
            path: 'playerreveal',
            component: PlayerRevealLayoutPageComponent,
            children: [
              { path: '', component: PlayerRevealPageComponent },
              { path: 'leaderboard', component: PlayerRevealLeaderboardPageComponent },
            ],
          },
          {
            path: 'photoscramble',
            component: PhotoScrambleLayoutPageComponent,
            children: [
              { path: '', component: PhotoScramblePageComponent },
              { path: 'leaderboard', component: PhotoScrambleLeaderboardPageComponent },
            ],
          },
          {
            path: 'acejump',
            component: AceJumpLayoutPageComponent,
            children: [
              { path: '', component: AceJumpPageComponent },
              { path: 'leaderboard', component: AceJumpLoaderboardPageComponent },
            ],
          },
          { path: 'polls', component: PollsPageComponent },
          {
            path: 'trivia',
            component: QuizLayoutPageComponent,
            children: [
              { path: '', component: QuizPageComponent },
              { path: 'leaderboard', component: QuizLeaderboardPageComponent },
            ],
          },
        ],
      },

      // Club Pages
      {
        path: 'checkout',
        children: [
          {
            path: '',
            component: CartCheckoutPageComponent,
            resolve: {
              member: MemberResolver,
              address: AddressResolver,
              country: CountryResolver,
            },
          },
          {
            path: 'success',
            component: CartCheckoutSuccessPageComponent,
          },
          {
            path: 'results',
            component: CartCheckoutResultsPageComponent,
          },
        ],
        resolve: {
          address: AddressResolver,
        },
      },
      {
        path: 'subscriptions',
        children: [
          {
            path: '',
            component: SubscriptionsPageComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
            },
          },
          {
            path: 'checkout',
            component: CartCheckoutPagePortalProductsComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
              country: CountryResolver,
              member: MemberResolver,
            },
          },
        ],
      },
      { path: 'notificationmanagement', component: NotificationManagementPageComponent },
      // { path: 'earn', component: EarnPageComponent },
      { path: '404page', component: NotFoundPageComponent },
      { path: 'signin-ticketmaster', component: TmCallbackComponent },
      {
        path: 'provider/:providerid',
        component: ProviderCallbackComponent,
      },
    ],
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent },
  { path: 'login/accounts', component: LoginAccountsPageComponent },
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [
      provideRegistrationConfig({
        routes: [
          // Custom pages
          {
            path: 'children',
            data: { minAge: 0, maxAge: 15 },
          },
          {
            path: 'payment',
            children: [
              {
                path: '',
                component: CustomRegistrationPaymentComponent,
                data: { registrationStep: StepType.Payment },
                resolve: { member: MemberResolver, address: AddressResolver },
              },
              {
                resolve: { member: MemberResolver, address: AddressResolver, registrationStep: RegistrationStepResolver },
                path: 'result',
                component: RegistrationPaymentCallbackPageComponent,
              },
            ],
          },
          {
            path: 'success',
            component: RegisterSuccessComponent,
          },
          {
            path: 'optoutalerts',
            component: RegistrationOptOutAlertsComponent,
          },
        ],
      }),
    ],
  },
  { path: 'callback', component: AuthCallbackComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'tm-auth-callback', component: TmAuthCallbackComponent },
  { path: 'sa-callback', component: SportsAllianceCallbackComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', bindToComponentInputs: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
